import classNames from "classnames"
import PropTypes from "prop-types"
import React, { useState } from "react"
import { useMutation } from "react-query"
import { dollarsToCents } from "src/main/Billing/Items/helpers"

import { updateTipPresets } from "src/api/Settings"

import { useToast } from "src/hooks/use_toast"

import { centsToDollars } from "src/utils/UnitConversion"

const MODES = [
  {
    value: "fixed",
    label: "Fixed amounts",
    description: "Show three dollar amounts",
  },
  {
    value: "percentage",
    label: "Percentages",
    description: "Show three percentages",
  },
  {
    value: "smart",
    label: "Smart",
    description: "Dollars for small purchases, percentages for larger",
  },
]

const MODE_DEFAULTS = {
  fixedAmounts: ["5.00", "10.00", "15.00"],
  percentages: ["5", "10", "15"],
  smartAmounts: ["1.00", "2.00", "5.00"],
  smartPercentages: ["5", "10", "15"],
  smartThreshold: "25.00",
}

const AMOUNT_MAX_CENTS = 10000
const PERCENTAGE_MAX = 50
const THRESHOLD_MAX_CENTS = 1000000
const AMOUNT_SOFT_DOLLARS = 25
const PERCENTAGE_SOFT = 25

// Canonical (stored) representation of an input. Validation, duplicate
// detection, the reader preview, and the save payload all run off these so the
// client agrees with the server, which validates the rounded integer values.
const toCents = (value) => (value === "" ? null : dollarsToCents(value))
const toPercent = (value) => {
  if (value === "") return null
  const number = Number(value)
  return Number.isNaN(number) ? null : Math.round(number)
}

const buildInitialValues = (tipPresets) => {
  const values = { ...MODE_DEFAULTS }

  if (tipPresets.mode === "fixed" && tipPresets.fixed_amounts) {
    values.fixedAmounts = tipPresets.fixed_amounts.map(centsToDollars)
  } else if (tipPresets.mode === "percentage" && tipPresets.percentages) {
    values.percentages = tipPresets.percentages.map(String)
  } else if (tipPresets.mode === "smart") {
    if (tipPresets.fixed_amounts) {
      values.smartAmounts = tipPresets.fixed_amounts.map(centsToDollars)
    }
    if (tipPresets.percentages) {
      values.smartPercentages = tipPresets.percentages.map(String)
    }
    if (tipPresets.smart_tip_threshold != null) {
      values.smartThreshold = centsToDollars(tipPresets.smart_tip_threshold)
    }
  }

  return values
}

const buildPayload = (mode, values) => {
  switch (mode) {
    case "percentage":
      return { mode, percentages: values.percentages.map(toPercent) }
    case "smart":
      return {
        mode,
        fixed_amounts: values.smartAmounts.map(toCents),
        percentages: values.smartPercentages.map(toPercent),
        smart_tip_threshold: toCents(values.smartThreshold),
      }
    default:
      return {
        mode: "fixed",
        fixed_amounts: values.fixedAmounts.map(toCents),
      }
  }
}

const centsError = (value, maxCents, maxMessage) => {
  if (value === "") return "Required."
  const cents = toCents(value)
  if (cents == null || cents <= 0) return "Must be a positive number."
  if (cents > maxCents) return maxMessage
  return null
}

const amountError = (value) =>
  centsError(value, AMOUNT_MAX_CENTS, "Maximum is $100 per option.")

const thresholdError = (value) =>
  centsError(value, THRESHOLD_MAX_CENTS, "Maximum is $10,000.")

const percentageError = (value) => {
  if (value === "") return "Required."
  const percent = toPercent(value)
  if (percent == null || percent <= 0) return "Must be a positive number."
  if (percent > PERCENTAGE_MAX) return "Maximum is 50% per option."
  return null
}

const amountWarning = (value) =>
  Number(value) > AMOUNT_SOFT_DOLLARS
    ? "This amount is unusually high. Most marinas keep tips under $25."
    : null

const percentageWarning = (value) =>
  Number(value) > PERCENTAGE_SOFT
    ? "This percentage is unusually high. Most marinas keep tips under 25%."
    : null

const duplicateFlags = (rawValues, toCanonical) => {
  const counts = {}
  rawValues.forEach((value) => {
    const key = toCanonical(value)
    if (key == null) return
    counts[key] = (counts[key] || 0) + 1
  })

  return rawValues.map((value) => {
    const key = toCanonical(value)
    return key != null && counts[key] > 1
  })
}

const validateArray = (rawValues, validator, toCanonical) => {
  const errors = rawValues.map(validator)
  const duplicates = duplicateFlags(rawValues, toCanonical)
  return {
    errors,
    duplicates,
    hasHardError: errors.some(Boolean) || duplicates.some(Boolean),
  }
}

const DUPLICATE_MESSAGE =
  "Two of your presets are the same. Boaters will see duplicate options."

const TipPresetsEditor = ({ tipPresets, onSaved }) => {
  const showToast = useToast()
  const [mode, setMode] = useState(tipPresets.mode || "fixed")
  const [values, setValues] = useState(() => buildInitialValues(tipPresets))

  const { mutate, isLoading } = useMutation(
    (data) => updateTipPresets({ data }),
    {
      onSuccess: (data) => {
        onSaved(data.tipPresets)
        showToast("Tip presets saved", { type: "success" })
      },
      onError: (error) =>
        showToast(
          error.message ||
            "Something went wrong. Please reload the page and contact mayday@dockwa.com if the issue persists.",
          { type: "error" }
        ),
    }
  )

  const updateArrayValue = (key, index, value) =>
    setValues((prev) => {
      const next = [...prev[key]]
      next[index] = value
      return { ...prev, [key]: next }
    })

  const handleCancel = () => {
    setMode(tipPresets.mode || "fixed")
    setValues(buildInitialValues(tipPresets))
  }

  const fixed = validateArray(values.fixedAmounts, amountError, toCents)
  const percentage = validateArray(
    values.percentages,
    percentageError,
    toPercent
  )
  const smartAmounts = validateArray(values.smartAmounts, amountError, toCents)
  const smartPercentages = validateArray(
    values.smartPercentages,
    percentageError,
    toPercent
  )
  const smartThresholdError = thresholdError(values.smartThreshold)

  const hasHardError =
    (mode === "fixed" && fixed.hasHardError) ||
    (mode === "percentage" && percentage.hasHardError) ||
    (mode === "smart" &&
      (smartAmounts.hasHardError ||
        smartPercentages.hasHardError ||
        Boolean(smartThresholdError)))

  const canSave = !hasHardError && !isLoading

  const handleSave = () => mutate({ tip_presets: buildPayload(mode, values) })

  const optionInput = ({
    ariaLabel,
    value,
    onChange,
    affix,
    affixSide,
    step,
    invalid,
    warning,
  }) => (
    <div
      className={classNames(
        "rounded-md flex items-center gap-1 border bg-white px-2 py-1.5",
        invalid
          ? "border-red-400"
          : warning
          ? "border-amber-400"
          : "border-gray-300"
      )}
    >
      {affixSide === "left" && <span className="text-gray-500">{affix}</span>}
      <input
        type="number"
        min="0"
        step={step}
        aria-label={ariaLabel}
        className="w-full bg-transparent outline-none"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onWheel={(event) => event.target.blur()}
      />
      {affixSide === "right" && <span className="text-gray-500">{affix}</span>}
    </div>
  )

  const dollarColumn = ({
    key,
    index,
    label,
    ariaLabel,
    validation,
    warnFn,
  }) => {
    const error = validation.errors[index]
    const duplicate = validation.duplicates[index]
    const warning = !error && !duplicate ? warnFn(values[key][index]) : null

    return (
      <div key={ariaLabel}>
        <div className="mb-1 text-sm text-gray-600">{label}</div>
        {optionInput({
          ariaLabel,
          value: values[key][index],
          onChange: (value) => updateArrayValue(key, index, value),
          affix: "$",
          affixSide: "left",
          step: "0.01",
          invalid: Boolean(error) || duplicate,
          warning: Boolean(warning),
        })}
        {error && <p className="mt-1 text-sm text-red-600">✕ {error}</p>}
        {warning && <p className="text-amber-600 mt-1 text-sm">⚠ {warning}</p>}
      </div>
    )
  }

  const percentColumn = ({ key, index, label, ariaLabel, validation }) => {
    const error = validation.errors[index]
    const duplicate = validation.duplicates[index]
    const warning =
      !error && !duplicate ? percentageWarning(values[key][index]) : null

    return (
      <div key={ariaLabel}>
        <div className="mb-1 text-sm text-gray-600">{label}</div>
        {optionInput({
          ariaLabel,
          value: values[key][index],
          onChange: (value) => updateArrayValue(key, index, value),
          affix: "%",
          affixSide: "right",
          step: "1",
          invalid: Boolean(error) || duplicate,
          warning: Boolean(warning),
        })}
        {error && <p className="mt-1 text-sm text-red-600">✕ {error}</p>}
        {warning && <p className="text-amber-600 mt-1 text-sm">⚠ {warning}</p>}
      </div>
    )
  }

  const duplicateBanner = (
    <div className="rounded-md mt-3 bg-red-50 p-2 text-sm text-red-600">
      ✕ {DUPLICATE_MESSAGE}
    </div>
  )

  const previewDollars = (value) => `$${centsToDollars(toCents(value))}`
  const previewPercent = (value) => `${toPercent(value)}%`

  const readerPreview = () => {
    if (hasHardError) return null

    if (mode === "fixed") {
      return `Reader will show: ${values.fixedAmounts
        .map(previewDollars)
        .join(", ")}, Custom`
    }
    if (mode === "percentage") {
      return `Reader will show: ${values.percentages
        .map(previewPercent)
        .join(", ")}, Custom`
    }
    const threshold = previewDollars(values.smartThreshold)
    const amounts = values.smartAmounts.map(previewDollars).join(", ")
    const percentages = values.smartPercentages.map(previewPercent).join(", ")
    return `Below ${threshold}, reader shows ${amounts}, Custom. At ${threshold} or above, reader shows ${percentages}, Custom.`
  }

  return (
    <div className="mt-6">
      <h3 className="mb-1 mt-0 text-lg font-semibold">Tip presets</h3>
      <p className="mb-4 mt-0 text-sm text-gray-600">
        Choose how tip options appear on your card reader. Boaters always see
        three buttons plus a custom amount.
      </p>

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {MODES.map((option) => (
          <label
            key={option.value}
            className={classNames(
              "flex cursor-pointer items-start gap-2 rounded-lg border p-3",
              mode === option.value
                ? "border-blue-500 bg-blue-50"
                : "border-gray-200 bg-white"
            )}
          >
            <input
              type="radio"
              name="tipMode"
              className="mt-1"
              aria-label={option.label}
              value={option.value}
              checked={mode === option.value}
              onChange={() => setMode(option.value)}
            />
            <span>
              <span className="block font-semibold text-gray-900">
                {option.label}
              </span>
              <span className="block text-sm text-gray-500">
                {option.description}
              </span>
            </span>
          </label>
        ))}
      </div>

      {mode === "fixed" && (
        <>
          <div className="grid grid-cols-3 gap-3">
            {values.fixedAmounts.map((_, index) =>
              dollarColumn({
                key: "fixedAmounts",
                index,
                label: `Option ${index + 1}`,
                ariaLabel: `Fixed tip amount ${index + 1}`,
                validation: fixed,
                warnFn: amountWarning,
              })
            )}
          </div>
          {fixed.duplicates.some(Boolean) && duplicateBanner}
        </>
      )}

      {mode === "percentage" && (
        <>
          <div className="grid grid-cols-3 gap-3">
            {values.percentages.map((_, index) =>
              percentColumn({
                key: "percentages",
                index,
                label: `Option ${index + 1}`,
                ariaLabel: `Tip percentage ${index + 1}`,
                validation: percentage,
              })
            )}
          </div>
          {percentage.duplicates.some(Boolean) && duplicateBanner}
        </>
      )}

      {mode === "smart" && (
        <div className="flex flex-col gap-4">
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Below threshold (fixed amounts)
            </div>
            <div className="grid grid-cols-3 gap-3">
              {values.smartAmounts.map((_, index) =>
                dollarColumn({
                  key: "smartAmounts",
                  index,
                  label: `Option ${index + 1}`,
                  ariaLabel: `Below-threshold amount ${index + 1}`,
                  validation: smartAmounts,
                  warnFn: amountWarning,
                })
              )}
            </div>
            {smartAmounts.duplicates.some(Boolean) && duplicateBanner}
          </div>

          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              At or above threshold (percentages)
            </div>
            <div className="grid grid-cols-3 gap-3">
              {values.smartPercentages.map((_, index) =>
                percentColumn({
                  key: "smartPercentages",
                  index,
                  label: `Option ${index + 1}`,
                  ariaLabel: `At-or-above-threshold percentage ${index + 1}`,
                  validation: smartPercentages,
                })
              )}
            </div>
            {smartPercentages.duplicates.some(Boolean) && duplicateBanner}
          </div>

          <div className="rounded-md flex flex-col gap-2 border border-gray-200 p-3 sm:flex-row sm:items-start sm:gap-4">
            <div className="sm:w-48">
              <div
                className="mb-1 text-sm text-gray-600"
                title="Below this subtotal the reader shows dollar tips; at or above it, percentages."
              >
                Switch to percentages above ⓘ
              </div>
              {optionInput({
                ariaLabel: "Switch to percentages above",
                value: values.smartThreshold,
                onChange: (value) =>
                  setValues((prev) => ({ ...prev, smartThreshold: value })),
                affix: "$",
                affixSide: "left",
                step: "0.01",
                invalid: Boolean(smartThresholdError),
                warning: false,
              })}
              {smartThresholdError && (
                <p className="mt-1 text-sm text-red-600">
                  ✕ {smartThresholdError}
                </p>
              )}
            </div>
            <p className="mt-0 text-sm text-gray-500">
              Below this subtotal, the reader shows the fixed dollar buttons. At
              or above it, the reader shows the percentages.
            </p>
          </div>
        </div>
      )}

      {!hasHardError && (
        <p className="mt-3 text-sm italic text-gray-500">{readerPreview()}</p>
      )}

      <div className="mt-4 flex items-center gap-4">
        <button
          type="button"
          className="btn btn-primary"
          disabled={!canSave}
          onClick={handleSave}
        >
          Save tip presets
        </button>
        <button
          type="button"
          className="cursor-pointer border-0 bg-transparent p-0 text-gray-600 hover:underline"
          onClick={handleCancel}
        >
          Cancel
        </button>
        {hasHardError && (
          <span className="text-sm text-gray-500">
            Fix errors above to save.
          </span>
        )}
      </div>
    </div>
  )
}

TipPresetsEditor.propTypes = {
  tipPresets: PropTypes.shape({
    mode: PropTypes.oneOf(["fixed", "percentage", "smart"]).isRequired,
    fixed_amounts: PropTypes.arrayOf(PropTypes.number),
    percentages: PropTypes.arrayOf(PropTypes.number),
    smart_tip_threshold: PropTypes.number,
  }).isRequired,
  onSaved: PropTypes.func,
}

TipPresetsEditor.defaultProps = {
  onSaved: () => {},
}

export default TipPresetsEditor
